import React from "react";
import { Box, Button, Container, Stack, Typography, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AnimatedInView from "./AnimatedInView";

interface HeroCta {
  label: string;
  to?: string;
  href?: string;
  icon?: React.ReactNode;
}

interface GradientHeroProps {
  eyebrow?: string;
  title: React.ReactNode;
  subtitle?: string;
  primaryCta?: HeroCta;
  secondaryCta?: HeroCta;
  /** Short band for inner pages */
  compact?: boolean;
  /** Extra content below the CTAs (e.g. rating badge) or to the right on md+ */
  children?: React.ReactNode;
  rightSlot?: React.ReactNode;
}

const CtaButton: React.FC<{ cta: HeroCta; variant: "contained" | "outlined" }> = ({
  cta,
  variant,
}) => {
  const common = {
    variant,
    size: "large" as const,
    startIcon: cta.icon,
    sx:
      variant === "contained"
        ? { px: 4, py: 1.4, fontSize: "1rem" }
        : {
            px: 4,
            py: 1.4,
            fontSize: "1rem",
            color: "common.white",
            borderColor: "rgba(255,255,255,0.4)",
            "&:hover": {
              borderColor: "common.white",
              backgroundColor: "rgba(255,255,255,0.08)",
            },
          },
  };
  if (cta.href) {
    return (
      <Button {...common} color="secondary" href={cta.href} target="_blank" rel="noopener noreferrer">
        {cta.label}
      </Button>
    );
  }
  return (
    <Button {...common} color="secondary" component={RouterLink} to={cta.to || "/"}>
      {cta.label}
    </Button>
  );
};

/** Full-bleed gradient hero with a terracotta glow, eyebrow, headline and CTAs. */
const GradientHero: React.FC<GradientHeroProps> = ({
  eyebrow,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  compact = false,
  children,
  rightSlot,
}) => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        background: theme.custom.gradients.hero,
        color: "common.white",
        py: compact ? { xs: 6, md: 8 } : { xs: 9, md: 13 },
      }}
    >
      {/* Terracotta glow overlay */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background: theme.custom.gradients.heroGlow,
          pointerEvents: "none",
        }}
      />
      {/* Subtle dot grid */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          maskImage: "linear-gradient(to bottom, black, transparent 80%)",
          pointerEvents: "none",
        }}
      />
      <Container maxWidth="lg" sx={{ position: "relative", px: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: rightSlot ? { xs: "1fr", md: "7fr 5fr" } : "1fr",
            gap: { xs: 5, md: 6 },
            alignItems: "center",
          }}
        >
          <AnimatedInView variant="fadeUp">
            <Box sx={{ textAlign: rightSlot ? "left" : "center" }}>
              {eyebrow && (
                <Typography
                  variant="overline"
                  component="p"
                  sx={{ color: "secondary.light", mb: 1.5 }}
                >
                  {eyebrow}
                </Typography>
              )}
              <Typography
                variant={compact ? "h2" : "h1"}
                component="h1"
                sx={{ mb: 2.5, maxWidth: rightSlot ? 640 : 820, mx: rightSlot ? 0 : "auto" }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    fontWeight: 400,
                    color: "rgba(255,255,255,0.82)",
                    maxWidth: 640,
                    mx: rightSlot ? 0 : "auto",
                    mb: primaryCta ? 4 : 0,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
              {(primaryCta || secondaryCta) && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  justifyContent={rightSlot ? "flex-start" : "center"}
                  alignItems="center"
                >
                  {primaryCta && <CtaButton cta={primaryCta} variant="contained" />}
                  {secondaryCta && <CtaButton cta={secondaryCta} variant="outlined" />}
                </Stack>
              )}
              {children}
            </Box>
          </AnimatedInView>
          {rightSlot && (
            <AnimatedInView variant="fade" delay={0.2}>
              <Box sx={{ display: { xs: "none", md: "block" } }}>{rightSlot}</Box>
            </AnimatedInView>
          )}
        </Box>
      </Container>
    </Box>
  );
};

/** Gradient-accented span for hero headlines */
export const GradientText: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        background: theme.custom.gradients.textAccent,
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        WebkitTextFillColor: "transparent",
        color: "transparent",
      }}
    >
      {children}
    </Box>
  );
};

export default GradientHero;
